import { Controller } from "@hotwired/stimulus"

// Development-only knob tuner for the public What's New page
// (pages/whats_new, mounted with ?tune=1). Drag a phone's screenshot up or
// down to pick --wn-shot-shift. Only the screenshot moves: the device and
// its --wn-crop stay exactly as the stylesheet has them. The panel lists
// every chapter's rule in the stylesheet's own format, ready to paste.
const FRAME_WIDTH = 433
const DEFAULT_CROP = 882
const MIN_SHIFT = -700
const MAX_SHIFT = 8

export default class extends Controller {
  connect() {
    this.columns = [...document.querySelectorAll("[class*='wn-shot-']")]
    this.state = new Map(this.columns.map((column) => [column, this.readShift(column)]))
    this.crops = new Map(this.columns.map((column) => [column, this.readCrop(column)]))
    this.columns.forEach((column) => this.makeDraggable(column))
    this.buildPanel()
    this.render()
  }

  disconnect() {
    this.panel?.remove()
  }

  readShift(column) {
    const value = parseFloat(getComputedStyle(column).getPropertyValue("--wn-shot-shift"))
    return Number.isFinite(value) ? value : 0
  }

  readCrop(column) {
    const value = parseFloat(getComputedStyle(column).getPropertyValue("--wn-crop"))
    return Number.isFinite(value) ? value : DEFAULT_CROP
  }

  nameOf(column) {
    const knob = [...column.classList].find((name) => name.startsWith("wn-shot-"))
    return knob.replace("wn-shot-", "")
  }

  makeDraggable(column) {
    const phone = column.querySelector(".wn-phone")
    phone.style.cursor = "ns-resize"
    phone.style.touchAction = "none"
    phone.addEventListener("pointerdown", (event) => {
      event.preventDefault()
      // Light and dark frames both live in the phone; the hidden one is 0
      // wide, so measure whichever is actually on screen.
      const width = Math.max(...[...phone.querySelectorAll("svg")].map((svg) => svg.getBoundingClientRect().width))
      if (!(width > 0)) return
      const unit = width / FRAME_WIDTH
      const startY = event.clientY
      const startShift = this.state.get(column)
      const move = (moveEvent) => this.apply(column, startShift + (moveEvent.clientY - startY) / unit)
      const stop = () => {
        window.removeEventListener("pointermove", move)
        window.removeEventListener("pointerup", stop)
      }
      window.addEventListener("pointermove", move)
      window.addEventListener("pointerup", stop)
    })
  }

  apply(column, shift) {
    const clamped = Math.round(Math.min(MAX_SHIFT, Math.max(MIN_SHIFT, shift)) * 10) / 10
    this.state.set(column, clamped)
    column.style.setProperty("--wn-shot-shift", `${clamped}px`)
    this.render()
  }

  css() {
    const names = this.columns.map((column) => this.nameOf(column))
    const width = Math.max(...names.map((name) => name.length))
    return this.columns.map((column, index) => {
      const shift = this.state.get(column)
      const selector = `.wn-shot-${names[index]}`.padEnd(width + 9)
      return `  ${selector} { --wn-phone-shift: 0%; --wn-phone-lift: 0%; --wn-shot-shift: ${shift}px; --wn-crop: ${this.crops.get(column)}; }`
    }).join("\n")
  }

  buildPanel() {
    this.panel = document.createElement("div")
    this.panel.style.cssText = "position:fixed;inset-inline-end:16px;bottom:16px;z-index:9999;width:min(560px,calc(100vw - 32px));" +
      "background:#111827;color:#E5E7EB;border:1px solid #374151;border-radius:12px;padding:12px;font:12px/1.5 ui-monospace,monospace;box-shadow:0 10px 30px rgba(0,0,0,.4)"
    this.panel.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;font-family:system-ui">
        <b>Drag a phone up or down</b>
        <button type="button" data-copy style="background:#3B82F6;color:#fff;border-radius:8px;padding:4px 12px">Copy CSS</button>
      </div>
      <pre data-output style="white-space:pre;overflow-x:auto;margin:0;max-height:40vh"></pre>`
    this.output = this.panel.querySelector("[data-output]")
    const copy = this.panel.querySelector("[data-copy]")
    copy.addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(this.css())
        copy.textContent = "Copied"
      } catch (error) {
        console.error("[whats-new-tuner] copy failed", error)
        copy.textContent = "Select and copy"
      }
      setTimeout(() => { copy.textContent = "Copy CSS" }, 1500)
    })
    document.body.appendChild(this.panel)
  }

  render() {
    this.output.textContent = this.css()
  }
}
